import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AdminSelectionDetail, AdminSelectionsPage, AdminUserDetail, AdminUserRow, AdminUsersPage } from "../../types";
import {
  adminDeleteUser, adminGrantBlocks, adminRevokeBlocks,
  fetchAdminSelection, fetchAdminSelections, fetchAdminUser, fetchAdminUsers,
} from "../../utils/api";
import { useAppConfig } from "../../config/AppConfigContext";
import ResultScreen from "../selection/ResultScreen";
import { AppHeader, Banner, Icon } from "../common/ui";

/**
 * «Пользователи» (admin only): who uses the app and how far along each person
 * is — access per block with days left and how much of the period is used,
 * money paid, calculations, lessons, messages, last activity. Tap a card for
 * the detail: access history, payments, progress per chapter, recent clients.
 * Read-only; the numbers come from GET /admin/users (endpoints/admin_users.py).
 */
const FILTERS = ["all", "access", "free", "expired", "new", "admins"] as const;
const SORTS = ["activity", "created", "name"] as const;
const PAGE = 30;

function ago(iso: string | null, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!iso) return "—";
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return t("adminUsers.minAgo", { count: Math.max(1, min) });
  const h = Math.round(min / 60);
  if (h < 48) return t("adminUsers.hAgo", { count: h });
  return t("adminUsers.dAgo", { count: Math.round(h / 24) });
}

function money(paid: Record<string, string>): string {
  const sym: Record<string, string> = { USD: "$", RUB: "₽", BYN: "BYN" };
  return Object.entries(paid).map(([c, v]) => {
    const n = Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
    return c === "USD" ? `$${n}` : `${n} ${sym[c] ?? c}`;
  }).join(" · ");
}

/**
 * Grant / cancel / delete for one user. Every destructive step asks once more
 * in place (no browser confirm — it is unreliable inside Telegram).
 */
function UserActions({ u, token, isRu, onChanged, onDeleted }: {
  u: AdminUserRow; token: string; isRu: boolean;
  onChanged: (row: AdminUserRow) => void; onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const { config } = useAppConfig();
  const blocks = config.blocks ?? [];
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [confirm, setConfirm] = useState<null | { kind: "revoke"; block?: string } | { kind: "delete" }>(null);

  const run = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ kind: "success", text: ok }); setConfirm(null); }
    catch (e) {
      const m = String(e);
      setMsg({ kind: "error", text: m.includes("CANNOT_DELETE_ADMIN") ? t("adminUsers.errDeleteAdmin")
        : m.includes("CANNOT_DELETE_SELF") ? t("adminUsers.errDeleteSelf") : t("common.error") });
    } finally { setBusy(false); }
  };
  const nDays = Math.max(0, Math.min(3650, parseInt(days, 10) || 0));
  const title = (key: string) => {
    const b = blocks.find((x) => x.key === key);
    return b ? (isRu ? b.title_ru : b.title_en) : key;
  };

  return (
    <div className="user-actions">
      <p className="eyebrow" style={{ margin: "12px 0 6px" }}>{t("adminUsers.grantTitle")}</p>
      <div className="tier-durations">
        {blocks.map((b) => (
          <button key={b.key} type="button" aria-pressed={picked.has(b.key)}
            className={"tier-duration" + (picked.has(b.key) ? " is-on" : "")}
            onClick={() => setPicked((p) => { const n = new Set(p); if (n.has(b.key)) n.delete(b.key); else n.add(b.key); return n; })}>
            {isRu ? b.title_ru : b.title_en}
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        {[30, 90, 180, 365].map((d) => (
          <button key={d} type="button" className={"tier-duration" + (nDays === d ? " is-on" : "")} onClick={() => setDays(String(d))}>
            {t("adminUsers.daysLeft", { count: d })}
          </button>
        ))}
        <input className="user-actions__days" inputMode="numeric" value={days}
          onChange={(e) => setDays(e.target.value.replace(/\D/g, "").slice(0, 4))} aria-label={t("adminUsers.daysInput")} />
      </div>
      <button className="btn btn-primary btn-block" style={{ marginTop: 10 }} disabled={busy || !picked.size || !nDays}
        onClick={() => run(async () => { onChanged(await adminGrantBlocks(u.id, [...picked], nDays, token)); setPicked(new Set()); },
          t("adminUsers.granted"))}>
        <Icon name="sparkles" size={15} /> {t("adminUsers.grantBtn", { count: nDays })}
      </button>

      {u.access.length > 0 && (
        <>
          <p className="eyebrow" style={{ margin: "14px 0 6px" }}>{t("adminUsers.cancelTitle")}</p>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {u.access.map((a) => (
              <button key={a.block} type="button" className="btn btn-glass" style={{ padding: "6px 10px" }} disabled={busy}
                onClick={() => setConfirm({ kind: "revoke", block: a.block })}>
                <Icon name="x" size={13} /> {a.title}
              </button>
            ))}
            <button type="button" className="btn btn-glass" style={{ padding: "6px 10px" }} disabled={busy}
              onClick={() => setConfirm({ kind: "revoke" })}>
              <Icon name="x" size={13} /> {t("adminUsers.cancelAll")}
            </button>
          </div>
        </>
      )}

      {u.role !== "admin" && (
        <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 14, color: "var(--error)" }}
          disabled={busy} onClick={() => setConfirm({ kind: "delete" })}>
          <Icon name="trash" size={15} /> {t("adminUsers.deleteBtn")}
        </button>
      )}

      {confirm && (
        <div className="user-actions__confirm" role="alertdialog">
          <p className="t-sm" style={{ margin: "0 0 10px", lineHeight: 1.45 }}>
            {confirm.kind === "delete" ? t("adminUsers.confirmDelete", { name: u.name })
              : confirm.block ? t("adminUsers.confirmRevoke", { block: title(confirm.block), name: u.name })
              : t("adminUsers.confirmRevokeAll", { name: u.name })}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-glass" style={{ flex: 1 }} disabled={busy} onClick={() => setConfirm(null)}>{t("auth.cancel")}</button>
            <button className="btn btn-primary" style={{ flex: 1, background: "var(--error)" }} disabled={busy}
              onClick={() => confirm.kind === "delete"
                ? run(async () => { await adminDeleteUser(u.id, token); onDeleted(); }, t("adminUsers.deleted"))
                : run(async () => { onChanged(await adminRevokeBlocks(u.id, confirm.block ? [confirm.block] : undefined, token)); },
                  t("adminUsers.revoked"))}>
              {confirm.kind === "delete" ? t("adminUsers.deleteYes") : t("adminUsers.revokeYes")}
            </button>
          </div>
        </div>
      )}
      {msg && <div style={{ marginTop: 10 }}><Banner kind={msg.kind}>{msg.text}</Banner></div>}
    </div>
  );
}

function UserCard({ u: initial, token, isRu, onOpenCalc, onDeleted }: {
  u: AdminUserRow; token: string; isRu: boolean; onOpenCalc: (n: number) => void; onDeleted: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [u, setU] = useState(initial);
  useEffect(() => setU(initial), [initial]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [failed, setFailed] = useState(false);

  const toggle = () => {
    setOpen((o) => !o);
    if (!detail && !failed) fetchAdminUser(u.id, token).then(setDetail).catch(() => setFailed(true));
  };
  const date = (iso: string | null) => iso
    ? new Date(iso).toLocaleDateString(isRu ? "ru-RU" : "en-GB", { day: "numeric", month: "short", year: "2-digit" })
    : "—";

  const status = u.role === "admin" ? <span className="badge badge-violet">{t("adminUsers.admin")}</span>
    : u.access.length ? <span className="badge badge-success">{t("adminUsers.hasAccess")}</span>
    : u.had_access ? <span className="badge badge-amber">{t("adminUsers.expired")}</span>
    : <span className="badge">{t("adminUsers.free")}</span>;

  return (
    <div className="glass user-card">
      <button className="user-card__head" onClick={toggle} aria-expanded={open}>
        <span style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
          <span className="t-sm" style={{ fontWeight: 800, display: "block" }}>{u.name}</span>
          <span className="t-xs muted-2" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[u.username && `@${u.username}`, u.email, u.telegram_id && `tg ${u.telegram_id}`].filter(Boolean).join(" · ") || "—"}
          </span>
        </span>
        <span style={{ display: "grid", justifyItems: "end", gap: 4 }}>
          {status}
          <span className="t-xs muted-2">{ago(u.last_active_at, t)}</span>
        </span>
      </button>

      {u.access.length > 0 && (
        <div className="stack" style={{ "--gap": "8px", marginTop: 10 } as React.CSSProperties}>
          {u.access.map((a) => (
            <div key={a.block}>
              <div className="between t-xs">
                <span style={{ fontWeight: 700 }}>{a.title}</span>
                <span className="muted-2">{t("adminUsers.daysLeft", { count: a.days_left })} · {t("adminUsers.until", { date: date(a.expires_at) })}</span>
              </div>
              <div className="progress" style={{ marginTop: 4 }}>
                <div className="progress__fill" style={{ width: `${a.used_pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="user-card__stats">
        <span title={t("adminUsers.paid")}><Icon name="wallet" size={12} /> {u.payments ? money(u.paid) : "—"}</span>
        <span title={t("adminUsers.selections")}><Icon name="droplet" size={12} /> {u.selections}</span>
        <span title={t("adminUsers.lessons")}><Icon name="graduation" size={12} /> {u.lessons_done}/{u.lessons_total}</span>
        <span title={t("adminUsers.messages")}><Icon name="send" size={12} /> {u.messages}{u.works ? ` · ${t("adminUsers.worksShort", { count: u.works })}` : ""}</span>
        <span title={t("adminUsers.signIn")}>{u.telegram_linked ? "TG" : ""}{u.telegram_linked && u.google_linked ? "+" : ""}{u.google_linked ? "G" : ""}</span>
      </div>

      {open && (
        <div className="user-card__detail">
          {failed ? <Banner kind="error">{t("common.error")}</Banner> : !detail ? (
            <div style={{ display: "grid", placeItems: "center", padding: 12 }}><div className="spinner spinner-amber" /></div>
          ) : (
            <>
              <p className="t-xs muted-2" style={{ margin: "0 0 8px" }}>
                {t("adminUsers.joined", { date: date(u.created_at) })}
                {detail.last_message_at && ` · ${t("adminUsers.lastMessage", { when: ago(detail.last_message_at, t) })}`}
              </p>

              <UserActions u={u} token={token} isRu={isRu}
                onChanged={(row) => { setU(row); fetchAdminUser(u.id, token).then(setDetail).catch(() => {}); }}
                onDeleted={() => onDeleted(u.id)} />

              {detail.chapters.length > 0 && (
                <>
                  <p className="eyebrow" style={{ margin: "6px 0" }}>{t("adminUsers.chapters")}</p>
                  {detail.chapters.map((c) => (
                    <div key={c.section} className="between t-xs" style={{ marginBottom: 4 }}>
                      <span>{c.section === "intro" ? t("adminUsers.intro")
                        : t(`study.section${c.section.charAt(0).toUpperCase()}${c.section.slice(1)}`, { defaultValue: c.section })}</span>
                      <span className="muted-2">{t("adminUsers.chapterLine", { done: c.done, started: c.started, total: c.total })}</span>
                    </div>
                  ))}
                </>
              )}

              {detail.subscriptions.length > 0 && (
                <>
                  <p className="eyebrow" style={{ margin: "10px 0 6px" }}>{t("adminUsers.accessHistory")}</p>
                  {detail.subscriptions.map((s, i) => (
                    <div key={i} className="between t-xs" style={{ marginBottom: 4, gap: 8 }}>
                      <span>{s.title} <span className="muted-2">· {t(`adminUsers.src_${s.source}`, { defaultValue: s.source })}</span></span>
                      <span className={s.status === "active" ? "" : "muted-2"}>{date(s.started_at)} → {date(s.expires_at)}</span>
                    </div>
                  ))}
                </>
              )}

              {detail.payments.length > 0 && (
                <>
                  <p className="eyebrow" style={{ margin: "10px 0 6px" }}>{t("adminUsers.payments")}</p>
                  {detail.payments.map((p, i) => (
                    <div key={i} className="between t-xs" style={{ marginBottom: 4, gap: 8 }}>
                      <span>{p.plan_code || "—"} <span className="muted-2">· {t(`payments.status_${p.status}`, { defaultValue: p.status })}{p.environment !== "live" ? " · test" : ""}</span></span>
                      <span>{money({ [p.currency]: p.amount })} · {date(p.created_at)}</span>
                    </div>
                  ))}
                </>
              )}

              {detail.selections.length > 0 && (
                <>
                  <p className="eyebrow" style={{ margin: "10px 0 6px" }}>{t("adminUsers.recentSelections")}</p>
                  {detail.selections.map((s, i) => (
                    <button key={i} className="calc-link between t-xs" disabled={s.number == null}
                      onClick={() => s.number != null && onOpenCalc(s.number)}>
                      <span>{s.number != null && <b>№{s.number} </b>}{s.client_name || t("adminUsers.noClientName")}</span>
                      <span className="muted-2">{s.pigment_type === "mineral" ? "Mineral" : s.pigment_type === "hybrid" ? "Hybrid" : ""} · {date(s.created_at)}</span>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** One calculation exactly as the master saw it (same cleaning as their history). */
function CalcViewer({ number, token, onClose, theme, onThemeChange }: {
  number: number; token: string; onClose: () => void;
  theme: "dark" | "light"; onThemeChange: (t: "dark" | "light") => void;
}) {
  const { t } = useTranslation();
  const [d, setD] = useState<AdminSelectionDetail | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { fetchAdminSelection(number, token).then(setD).catch(() => setFailed(true)); }, [number, token]);
  if (!d) {
    return (
      <div className="beeapp__scroll">
        <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onClose} title={t("selection.calcNumber", { number })} />
        <div className="wrap content-cap" style={{ paddingTop: 12 }}>
          {failed ? <Banner kind="error">{t("adminUsers.calcNotFound", { number })}</Banner>
            : <div style={{ display: "grid", placeItems: "center", padding: 24 }}><div className="spinner spinner-amber" /></div>}
        </div>
      </div>
    );
  }
  return (
    <ResultScreen
      result={d.session.result}
      calcNumber={d.session.number}
      clientName={`${d.session.client_name || t("adminUsers.noClientName")} · ${t("adminUsers.master", { name: d.user_name })}`}
      createdAt={d.session.created_at}
      onExit={onClose}
      onHome={onClose}
      theme={theme}
      onThemeChange={onThemeChange}
    />
  );
}

/** Every saved calculation, newest first; search by number, client or master. */
function CalcsTab({ token, onOpenCalc, isRu }: { token: string; onOpenCalc: (n: number) => void; isRu: boolean }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState<AdminSelectionsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reqId = useRef(0);
  const load = useCallback((offset: number) => {
    const id = ++reqId.current;
    setLoading(true);
    setError(false);
    fetchAdminSelections({ q, offset }, token)
      .then((r) => { if (id === reqId.current) setPage((prev) => (offset && prev ? { ...r, items: [...prev.items, ...r.items] } : r)); })
      .catch(() => { if (id === reqId.current) setError(true); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [q, token]);
  useEffect(() => {
    const h = setTimeout(() => load(0), q ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, q]);
  const when = (iso: string | null) => iso
    ? new Date(iso).toLocaleString(isRu ? "ru-RU" : "en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <>
      <label className="field" style={{ display: "flex" }}>
        <Icon name="search" size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adminUsers.calcSearch")} inputMode="search"
          style={{ flex: 1, background: "transparent", border: 0, outline: "none", color: "inherit", font: "inherit" }} />
      </label>
      <p className="t-xs muted-2" style={{ margin: 0 }}>{page ? t("adminUsers.found", { count: page.total }) : ""}</p>
      {error && <Banner kind="error">{t("common.error")}</Banner>}
      {page?.items.map((c) => (
        <button key={c.number ?? Math.random()} className="glass calc-row" disabled={c.number == null}
          onClick={() => c.number != null && onOpenCalc(c.number)}>
          <span className="calc-row__num">№{c.number ?? "—"}</span>
          <span style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
            <span className="t-sm" style={{ fontWeight: 700, display: "block" }}>{c.client_name || t("adminUsers.noClientName")}</span>
            <span className="t-xs muted-2" style={{ display: "block" }}>
              {t("adminUsers.master", { name: c.user_name })} · {when(c.created_at)}
              {c.pigment_type ? ` · ${c.pigment_type === "mineral" ? "Mineral" : "Hybrid"}` : ""}
            </span>
            {c.mix && <span className="t-xs" style={{ display: "block", marginTop: 2 }}>{c.mix}</span>}
          </span>
          <Icon name="chevronRight" size={16} />
        </button>
      ))}
      {page && page.items.length === 0 && !loading && <p className="t-sm muted" style={{ textAlign: "center" }}>{t("adminUsers.empty")}</p>}
      {loading && <div style={{ display: "grid", placeItems: "center", padding: 12 }}><div className="spinner spinner-amber" /></div>}
      {page && !loading && page.items.length < page.total && (
        <button className="btn btn-glass btn-block" onClick={() => load(page.items.length)}>{t("adminUsers.more")}</button>
      )}
    </>
  );
}

export default function AdminUsers({ token, onExit, theme, onThemeChange }: {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const [sort, setSort] = useState<(typeof SORTS)[number]>("activity");
  const [page, setPage] = useState<AdminUsersPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reqId = useRef(0);
  const [tab, setTab] = useState<"users" | "calcs">("users");
  const [openCalc, setOpenCalc] = useState<number | null>(null);

  const load = useCallback((offset: number) => {
    const id = ++reqId.current;
    setLoading(true);
    setError(false);
    fetchAdminUsers({ q, filter, sort, offset, limit: PAGE }, token)
      .then((r) => {
        if (id !== reqId.current) return;
        setPage((prev) => (offset && prev ? { ...r, items: [...prev.items, ...r.items] } : r));
      })
      .catch(() => { if (id === reqId.current) setError(true); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [q, filter, sort, token]);

  // Typing searches after a short pause, not on every key.
  useEffect(() => {
    const h = setTimeout(() => load(0), q ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, q]);

  const s = page?.summary;
  if (openCalc != null) {
    return <CalcViewer number={openCalc} token={token} onClose={() => setOpenCalc(null)} theme={theme} onThemeChange={onThemeChange} />;
  }
  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("adminUsers.title")} />
      <div className="wrap content-cap" style={{ paddingTop: 6 }}>
        <div className="stack" style={{ "--gap": "14px" } as React.CSSProperties}>
          <div className="tier-durations" role="tablist">
            {(["users", "calcs"] as const).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k}
                className={"tier-duration" + (tab === k ? " is-on" : "")} onClick={() => setTab(k)}>
                {t(`adminUsers.tab_${k}`)}
              </button>
            ))}
          </div>
          {tab === "calcs" ? <CalcsTab token={token} onOpenCalc={setOpenCalc} isRu={isRu} /> : (<>
          {s && (
            <div className="users-summary">
              <div><b>{s.users}</b><span>{t("adminUsers.sumUsers")}</span></div>
              <div><b>{s.with_access}</b><span>{t("adminUsers.sumAccess")}</span></div>
              <div><b>{s.active_7d}</b><span>{t("adminUsers.sumActive")}</span></div>
              <div><b>{s.new_7d}</b><span>{t("adminUsers.sumNew")}</span></div>
            </div>
          )}

          <label className="field" style={{ display: "flex" }}>
            <Icon name="search" size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adminUsers.search")}
              style={{ flex: 1, background: "transparent", border: 0, outline: "none", color: "inherit", font: "inherit" }} />
          </label>

          <div className="tier-durations" role="radiogroup" aria-label={t("adminUsers.filter")}>
            {FILTERS.map((f) => (
              <button key={f} type="button" role="radio" aria-checked={filter === f}
                className={"tier-duration" + (filter === f ? " is-on" : "")} onClick={() => setFilter(f)}>
                {t(`adminUsers.f_${f}`)}
              </button>
            ))}
          </div>
          <div className="between t-xs muted-2">
            <span>{page ? t("adminUsers.found", { count: page.total }) : ""}</span>
            <span className="row" style={{ gap: 6 }}>
              {t("adminUsers.sortBy")}
              <select value={sort} onChange={(e) => setSort(e.target.value as (typeof SORTS)[number])}
                style={{ background: "transparent", color: "inherit", border: "1px solid var(--border)", borderRadius: 8, padding: "2px 6px" }}>
                {SORTS.map((o) => <option key={o} value={o}>{t(`adminUsers.s_${o}`)}</option>)}
              </select>
            </span>
          </div>

          {error && <Banner kind="error">{t("common.error")}</Banner>}
          {page?.items.map((u) => (
            <UserCard key={u.id} u={u} token={token} isRu={isRu} onOpenCalc={setOpenCalc}
              onDeleted={(id) => setPage((p) => (p ? { ...p, total: p.total - 1, items: p.items.filter((x) => x.id !== id) } : p))} />
          ))}
          {page && page.items.length === 0 && !loading && <p className="t-sm muted" style={{ textAlign: "center" }}>{t("adminUsers.empty")}</p>}
          {loading && <div style={{ display: "grid", placeItems: "center", padding: 12 }}><div className="spinner spinner-amber" /></div>}
          {page && !loading && page.items.length < page.total && (
            <button className="btn btn-glass btn-block" onClick={() => load(page.items.length)}>{t("adminUsers.more")}</button>
          )}
          </>)}
          <div style={{ height: 24 }} />
        </div>
      </div>
    </div>
  );
}
